import {
  makeChangeNoderunnerTransactions,
  makeKeyregOfflineTransactions,
  makeKeyregOnlineTransactions,
  makeWithdrawNoderunnerFeesTransactions,
} from "@/lib/clients";
import {
  base64ToUint8Array,
  capitalizeFirst,
  cn,
  formatNumber,
  shorten,
} from "@/lib/utils";
import { useWallet } from "@txnlab/use-wallet";
import {
  DSContractListing,
  DSContractState,
} from "@myth-finance/dualstake-ts-sdk";
import { useState, Dispatch, SetStateAction, useMemo } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  Coins,
  EthernetPort,
  RefreshCw,
  RefreshCwOff,
  UserPen,
  UserRound,
  Wrench,
} from "lucide-react";
import { AccountLink, ApplicationLink } from "@/components/links";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ConnectButton } from "@/components/connect-button";
import {
  TransactionDialog,
  transactionInner,
} from "@/components/transaction/transaction-dialog";
import algosdk from "algosdk";
import { cx } from "class-variance-authority";
import { CopyButton } from "@/components/copy-button";
import { clearCStateCache } from "@/hooks/use-cstate";
import { HelpButton } from "@/components/help-button";

export function NodeRunnerCard({
  cState,
  listing,
  role,
}: {
  cState: DSContractState;
  listing: DSContractListing;
  role: "Fee manager" | "Node runner";
}) {
  if (!cState || !listing) return null;
  return (
    <Card className={cx("w-[350px] relative")}>
      <CardHeader className="opacity-0 animate-fade-in-4">
        <div className="flex justify-between items-start">
          <div className="flex gap-2 text-3xl">
            <div className="pb-0 font-normal">Manage</div>
            <span className="text-muted-foreground">Node</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col items-center opacity-0 animate-fade-in-4">
        <div className="flex flex-col items-left w-full">
          <div className="flex w-full justify-between">
            <div className="text-muted-foreground">Your role</div>
            <div>{role}</div>
          </div>

          <div className="flex w-full justify-between">
            <div className="text-muted-foreground">Accrued Rewards</div>
            <div>{formatNumber(cState.noderunnerFees)} ALGO</div>
          </div>

          <div className="flex w-full mt-3 justify-between items-center">
            <div className="text-muted-foreground">
              {listing.lstName} Escrow
            </div>
            <div className="flex items-center -mr-2">
              {shorten(algosdk.getApplicationAddress(listing.appId), 6, false)}{" "}
              <CopyButton
                noLabel={true}
                buttonVariant="ghost"
                value={algosdk.getApplicationAddress(listing.appId)}
              />
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="justify-between opacity-0 animate-fade-in-4">
        <NodeRunnerManageDialog cState={cState} listing={listing} />
        <NodeClaimButton cState={cState} listing={listing} />
      </CardFooter>
      <EthernetPort
        size={160}
        strokeWidth={1.5}
        className="absolute pointer-events-none z-0 top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] opacity-10"
      />
    </Card>
  );
}

function NodeClaimButton({
  cState,
  listing,
}: {
  cState: DSContractState;
  listing: DSContractListing;
}) {
  const { activeAddress } = useWallet();

  return (
    <TransactionDialog
      type="operation"
      trigger={(onClick) => (
        <Button
          variant={cState.noderunnerFees === 0n ? "outline" : "default"}
          size="sm"
          onClick={onClick}
          disabled={cState.noderunnerFees === 0n}
        >
          <Coins />
          CLAIM
        </Button>
      )}
      onSuccess={() => {
        clearCStateCache(cState.appId);
      }}
      transactionBuilder={() =>
        makeWithdrawNoderunnerFeesTransactions(listing.appId, activeAddress!)
      }
      title={`Withdrawing node runner fees`}
    >
      {transactionInner(
        "withdrawal",
        <div className="flex flex-col gap-2">
          <div>
            You are going to <strong>withdraw node runner fees</strong> from the{" "}
            <span className="font-bold">{listing.lstName}</span> contract
          </div>
        </div>,
        <>
          <div className="flex justify-between gap-2 mb-2">
            <div className="flex items-center">
              <UserRound size={18} className="mr-1" /> Account
            </div>{" "}
            <span>{shorten(activeAddress!)}</span>
          </div>
          <div className="flex justify-between gap-2">
            <div className="flex items-center">
              <ChevronRight size={18} className="mr-1" />
              App Call
            </div>
            <span className="text-right">
              App <ApplicationLink id={listing.appId} />
            </span>
          </div>
          {cState.noderunnerFees !== 0n ? (
            <div className="flex justify-between gap-2">
              <div className="flex items-center ml-2">
                <ChevronLeft size={18} className="mr-1" /> Receiving
              </div>{" "}
              <span className="text-right">
                {formatNumber(cState.noderunnerFees)} ALGO
              </span>
            </div>
          ) : null}
        </>
      )}
    </TransactionDialog>
  );
}

export function NodeRunnerManageDialog({
  cState,
  listing,
}: {
  cState: DSContractState;
  listing: DSContractListing;
}) {
  const [mode, setMode] = useState<"" | "keyreg" | "transfer">("");
  const [open, setOpen] = useState(false);

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    setMode("");
  };
  const doClose = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger>
        <Button onClick={() => setOpen(true)} variant="outline" size="sm">
          <Wrench />
          MANAGE
        </Button>
      </DialogTrigger>
      <DialogContent>
        {mode === "" ? (
          <NRMSelect setMode={setMode} />
        ) : mode === "keyreg" ? (
          <NRMKeyreg doClose={doClose} cState={cState} listing={listing} />
        ) : (
          <NRMTransfer doClose={doClose} cState={cState} listing={listing} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function NRMSelect({
  setMode,
}: {
  setMode: Dispatch<SetStateAction<"" | "keyreg" | "transfer">>;
}) {
  return (
    <DialogHeader>
      <DialogTitle className="pb-2">Node runner management</DialogTitle>
      <DialogDescription className="gap-4 flex flex-col">
        <div>Select your operation type</div>
        <Button onClick={() => setMode("keyreg")}>Key registration</Button>
        <Button variant="outline" onClick={() => setMode("transfer")}>
          Transfer Noderunner role
        </Button>
      </DialogDescription>
    </DialogHeader>
  );
}

function NRMKeyreg({
  cState,
  listing,
  doClose,
}: {
  cState: DSContractState;
  listing: DSContractListing;
  doClose: () => void;
}) {
  const [mode, setMode] = useState<"offline" | "online" | "">("");

  if (mode !== "")
    return (
      <NRMKeyregDetails
        type={mode}
        listing={listing}
        cState={cState}
        doClose={doClose}
      />
    );

  return (
    <DialogHeader>
      <DialogTitle className="pb-2">Key registration</DialogTitle>
      <DialogDescription className="gap-4 flex flex-col">
        <div>Select your key registration transaction type</div>
        <Button onClick={() => setMode("online")}>Online</Button>
        <Button variant="outline" onClick={() => setMode("offline")}>
          Offline
        </Button>
      </DialogDescription>
    </DialogHeader>
  );
}

function NRMKeyregDetails({
  type,
  listing,
  doClose,
}: {
  type: "online" | "offline";
  cState: DSContractState;
  listing: DSContractListing;
  doClose: () => void;
}) {
  const { activeAddress } = useWallet();
  const Type = capitalizeFirst(type);
  const escrow = useMemo(
    () => (listing.appId ? algosdk.getApplicationAddress(listing.appId) : ""),
    [listing.appId]
  );
  const [selkey, setSelkey] = useState("");
  const [votekey, setVotekey] = useState("");
  const [sprfkey, setSprfkey] = useState("");
  const [fv, setFv] = useState<number | null>(null);
  const [lv, setLv] = useState<number | null>(null);
  const [kd, setKd] = useState<number | null>(null);

  const change: (
    setter: Dispatch<SetStateAction<string>>
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void =
    (setter) => (event) => {
      setter(event.currentTarget.value);
    };

  const changeNum: (
    setter: Dispatch<SetStateAction<number | null>>
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void =
    (setter) => (event) => {
      const { value } = event.currentTarget;
      const num = parseInt(value, 10);
      if (!isNaN(num)) {
        setter(num);
      } else if (value === "") {
        setter(null);
      }
    };

  const canSubmit =
    type === "offline" ||
    (fv !== undefined && lv && kd && selkey && sprfkey && votekey);

  if (type === "online" && listing.staked < 2_000_000n) {
    return (
      <DialogHeader>
        <DialogTitle className="pb-2">{Type} key registration</DialogTitle>
        <DialogDescription>
          <span className="text-foreground">Error: {listing.lstName} needs at least 2 ALGO staked</span> before it can register online.
        </DialogDescription>
      </DialogHeader>
    );
  }

  return (
    <DialogHeader>
      <DialogTitle className="pb-2">{Type} key registration</DialogTitle>
      <DialogDescription className="gap-4 flex flex-col">
        {type === "offline" ? (
          <div className="flex flex-col">
            <div>
              This will take the {listing.lstName} contract offline from
              consensus.
            </div>
            <div>
              Rewards will be suspended until it is registered online again.
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <div className="text-foreground">
                1. Create participation keys for the {listing.lstName} escrow
                account:
              </div>
              <div className="flex justify-center items-center">
                <div className="flex items-center">
                  <AccountLink address={escrow} />
                  <CopyButton
                    noLabel={true}
                    className="ml-1"
                    buttonVariant="ghost"
                    value={escrow}
                  />
                </div>
              </div>
              <div className="text-foreground">
                2. Enter participation key information here:
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Input
                onChange={change(setVotekey)}
                value={votekey}
                placeholder="Voting Key"
              />
              <Input
                value={selkey}
                onChange={change(setSelkey)}
                placeholder="Selection Key"
              />
              <Input
                onChange={change(setSprfkey)}
                value={sprfkey}
                placeholder="State Proof Key"
              />
              <Input
                onChange={changeNum(setFv)}
                value={fv ?? ""}
                placeholder="First Valid Round"
              />
              <Input
                onChange={changeNum(setLv)}
                value={lv ?? ""}
                placeholder="Last Valid Round"
              />
              <Input
                onChange={changeNum(setKd)}
                value={kd ?? ""}
                placeholder="Key Dillution"
              />
            </div>
          </>
        )}
        {type === "online" && !listing.incentiveEligible ? (
          <div className="text-foreground flex items-center">
            Note: You will pay a 2 ALGO incentive eligibility fee.{" "}
            <HelpButton title="Why do I need to pay 2A?">
              This fee is required by the Algorand network in order to register
              the account as eligible for staking rewards.
            </HelpButton>
          </div>
        ) : null}
        <TransactionDialog
          type="operation"
          trigger={(onClick) => (
            <ConnectButton
              className={cn(
                "inline-flex w-full items-center justify-center rounded-lg text-sm font-medium",
                "ring-offset-background transition-all",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                "disabled:pointer-events-none disabled:opacity-50",
                "bg-primary text-primary-foreground hover:bg-primary/90",
                "h-12 px-6 py-2",
                "relative"
              )}
              disabled={!canSubmit}
              onClick={onClick}
            >
              {type === "offline" ? (
                <RefreshCwOff size={18} className="mr-1.5" />
              ) : (
                <RefreshCw size={18} className="mr-1.5" />
              )}
              <span>Register {Type}</span>
            </ConnectButton>
          )}
          onSuccess={() => {
            clearCStateCache(listing.appId);
            doClose();
          }}
          transactionBuilder={() =>
            type == "offline"
              ? makeKeyregOfflineTransactions(listing.appId, activeAddress!)
              : makeKeyregOnlineTransactions(
                  listing.appId,
                  activeAddress!,
                  base64ToUint8Array(selkey),
                  base64ToUint8Array(votekey),
                  base64ToUint8Array(sprfkey),
                  fv!,
                  lv!,
                  kd!,
                  listing.incentiveEligible ? 0 : 2_000_000
                )
          }
          title={`${capitalizeFirst(type)} key registration`}
        >
          {transactionInner(
            "operation",
            <div className="flex flex-col gap-2">
              <div>
                You are going to{" "}
                <strong className="text-destructive">register {type}</strong>{" "}
                for the <span className="font-bold">{listing.lstName}</span>{" "}
                contract.{" "}
                {type === "online" && !listing.incentiveEligible ? (
                  <>This includes a 2 ALGO incentive eligibility fee.</>
                ) : null}
              </div>
            </div>,
            <>
              <div className="flex justify-between gap-2 mb-2">
                <div className="flex items-center">
                  <UserRound size={18} className="mr-1" /> Account
                </div>{" "}
                <span>{shorten(activeAddress!)}</span>
              </div>
              <div className="flex justify-between gap-2 text-foreground">
                <div className="flex items-center">
                  <ChevronRight size={18} className="mr-1" />
                  App call
                </div>
                <span className="text-right text-muted-foreground">
                  App <ApplicationLink id={listing.appId} />
                </span>
              </div>
              {type === "online" && !listing.incentiveEligible ? (
                <div className="flex justify-between gap-2 text-foreground">
                  <div className="flex items-center">
                    <ChevronRight size={18} className="mr-1" />
                    Network Fee
                  </div>
                  <span className="text-right text-muted-foreground">
                    2 ALGO
                  </span>
                </div>
              ) : null}
            </>
          )}
        </TransactionDialog>
      </DialogDescription>
    </DialogHeader>
  );
}

function NRMTransfer({
  cState,
  listing,
  doClose,
}: {
  cState: DSContractState;
  listing: DSContractListing;
  doClose: () => void;
}) {
  const { activeAddress } = useWallet();
  const [nextAddress, setNextAddress] = useState("");

  const changeAddress = ({
    currentTarget: { value: valueStr },
  }: React.ChangeEvent<HTMLInputElement>) => setNextAddress(valueStr);

  if (cState.noderunnerFees !== 0n) {
    return (
      <DialogHeader>
        <DialogTitle className="pb-4">Node runner role transfer</DialogTitle>
        <DialogDescription className="gap-4 flex flex-col">
          <div className="text-destructive">
            Current node runner has accrued fees.{" "}
          </div>
          <div className="flex w-full justify-between">
            <div className="text-muted-foreground">Accrued Rewards</div>
            <div>{formatNumber(cState.noderunnerFees)} ALGO</div>
          </div>
          <div className="text-foreground font-bold">
            Withdraw rewards before transferring the role.
          </div>
        </DialogDescription>
      </DialogHeader>
    );
  }

  return (
    <DialogHeader>
      <DialogTitle className="pb-4">Node runner role transfer</DialogTitle>
      <DialogDescription className="gap-4 flex flex-col">
        {cState.noderunnerFees !== 0n ? (
          <>
            <div>
              This action will automatically send accrued node-runner rewards.
            </div>
            <div className="flex w-full justify-between">
              <div className="text-muted-foreground">Accrued Rewards</div>
              <div>{formatNumber(cState.noderunnerFees)} ALGO</div>
            </div>
          </>
        ) : null}
        <Label
          className="text-foreground flex justify-between"
          htmlFor="new_noderunner"
        >
          New node runner address
        </Label>
        <Input onChange={changeAddress} />
        <div className="text-destructive">
          Warning: This operation can not be undone.
        </div>

        <TransactionDialog
          type="operation"
          trigger={(onClick) => (
            <ConnectButton
              className={cn(
                "inline-flex w-full items-center justify-center rounded-lg text-sm font-medium",
                "ring-offset-background transition-all",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                "disabled:pointer-events-none disabled:opacity-50",
                "bg-primary text-primary-foreground hover:bg-primary/90",
                "h-12 px-6 py-2",
                "relative"
              )}
              onClick={onClick}
            >
              <UserPen size={18} className="mr-1.5" />
              <span>Change Node runner</span>
            </ConnectButton>
          )}
          onSuccess={() => {
            clearCStateCache(cState.appId);
            doClose();
          }}
          transactionBuilder={() =>
            makeChangeNoderunnerTransactions(
              listing.appId,
              activeAddress!,
              nextAddress
            )
          }
          title={`Change node runner`}
        >
          {transactionInner(
            "operation",
            <div className="flex flex-col gap-2">
              <div>
                You are going to <strong>change node runner role</strong> for
                the <span className="font-bold">{listing.lstName}</span>{" "}
                contract
              </div>
            </div>,
            <>
              <div className="flex justify-between gap-2 mb-2">
                <div className="flex items-center">
                  <UserRound size={18} className="mr-1" /> Account
                </div>{" "}
                <span>{shorten(activeAddress!)}</span>
              </div>
              <div className="flex justify-between gap-2 text-foreground">
                <div className="flex items-center">
                  <ChevronRight size={18} className="mr-1" />
                  App call
                </div>
                <span className="text-right text-muted-foreground">
                  App <ApplicationLink id={listing.appId} />
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <div className="flex items-center pl-5">New node runner</div>
                <span className="text-right  text-foreground">
                  {shorten(nextAddress, 6)}
                </span>
              </div>
            </>
          )}
        </TransactionDialog>
      </DialogDescription>
    </DialogHeader>
  );
}
